import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select.tsx";
import useMutateData from "@/hooks/useMutateData";
import {postCandidate} from "@/api/candidates.jsx";
import {Switch} from "@/components/ui/switch";
import {Check, Minus} from "lucide-react";
import { getSkills } from "@/api/skills.jsx";

export default function CandidateCreate() {
    const navigate = useNavigate();
    const { mutate } = useMutateData();
    const [skills, setSkills] = useState([]);
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        knownAs: "",
        gender: "",
        nationality: "",
        dob: "",
        phoneNumber: "",
        email: "",
        niNumber: "",
        payrollNumber: "",
        payMethod: "",
        payrollMethod: "",
        status: "Onboarding",
        contactDate: "",
        mainSkillId: "",
        startDate: "",
        loginEnabled: false,
        addressAttributes: {
            line1: "",
            line2: "",
            line3: "",
            town: "",
            county: "",
            postcode: "",
        },
        bankAccountAttributes:{
            accountHolderName: "",
            sortCode: "",
            bankAccountNumber: "",
        }
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const skillsData = await getSkills();
                setSkills(skillsData || []);
            } catch (error) {
                console.error("Failed to fetch skills:", error);
            }
        };
        fetchSkills();
    }, []);

    useEffect(() => {
        const fetchSkills = async () => {
            try {
                const skillsData = await getSkills();
                setSkills(skillsData || []);
            } catch (error) {
                console.error("Failed to fetch skills:", error);
            }
        };
        fetchSkills();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.startsWith("addressAttributes.")) {
            setFormData((prev) => ({
                ...prev,
                addressAttributes: {
                    ...prev.addressAttributes,
                    [name.split(".")[1]]: value,
                },
            }));
        } else if (name.startsWith("bankAccountAttributes")) {
            // ✅ Handle bankAccount fields
            setFormData((prev) => ({
                ...prev,
                bankAccountAttributes: {
                    ...prev.bankAccountAttributes,
                    [name.split(".")[1]]: value,
                },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleToggle = (checked) => {
        setFormData((prev) => ({ ...prev, loginEnabled: checked }));
    };

    // Helper component to display validation errors for a field
    const ValidationError = ({ field }) => {
        if (!validationErrors[field]) return null;
        return (
            <div className="text-red-600 text-sm mt-1">
                {Array.isArray(validationErrors[field]) 
                    ? validationErrors[field].join(", ")
                    : validationErrors[field]
                }
            </div>
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setValidationErrors({});

        try {
            const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || "https://ramsden-admin.fly.dev/api/v1"}/admin/candidates`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
                },
                credentials: "include",
                body: JSON.stringify({ candidate: formData })
            });

            const result = await response.json();
            
            if (response.ok && result.success !== false) {
                navigate(`/admin/candidate/${result.candidate?.slugId || result.slugId}`);
            } else {
                // Handle validation errors from Rails
                if (result.errors) {
                    setValidationErrors(result.errors);
                    setError("Please fix the validation errors below.");
                } else {
                    setError(result.message || "❌ Failed to create candidate. Please try again.");
                }
            }
        } catch (err) {
            console.error("Error creating candidate:", err);
            setError("❌ Network error. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Create Candidate</h2>
            {error && <p className="text-red-600 mb-4">{error}</p>}
            <div className="bg-white shadow rounded-xl p-6">
                <form onSubmit={handleSubmit} className="space-y-4">

                    <div>
                        <label className="font-medium block mb-1">Enable Login</label>
                        <div className="flex items-center space-x-4">
                            <Switch
                                checked={formData.loginEnabled}
                                onCheckedChange={handleToggle}
                                className={"data-[state=checked]:bg-brand-500"}
                            />
                            <div className="flex items-center space-x-1 text-sm font-medium">
                                {formData.loginEnabled ? (
                                    <>
                                        <Check className="w-4 h-4 text-green-600" />
                                        <span className="text-green-700">Can Login</span>
                                    </>
                                ) : (
                                    <>
                                        <Minus className="w-4 h-4 text-amber-500" />
                                        <span className="text-amber-500">Cannot Login</span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Name Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-medium block mb-1">First Name</label>
                            <Input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required />
                            <ValidationError field="firstName" />
                        </div>
                        <div>
                            <label className="font-medium block mb-1">Last Name</label>
                            <Input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required />
                            <ValidationError field="lastName" />
                        </div>
                    </div>

                    <div>
                        <label className="font-medium block mb-1">Known As</label>
                        <Input type="text" name="knownAs" value={formData.knownAs} onChange={handleChange} />
                    </div>

                    {/* Contact Fields */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-medium block mb-1">Phone Number</label>
                            <Input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} />
                            <ValidationError field="phoneNumber" />
                        </div>
                        <div>
                            <label className="font-medium block mb-1">Email Address</label>
                            <Input type="email" name="email" value={formData.email} onChange={handleChange} required />
                            <ValidationError field="email" />
                        </div>
                    </div>

                    {/* Nationality & Gender */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-medium block mb-1">Nationality</label>
                            <Input type="text" name="nationality" value={formData.nationality} onChange={handleChange} />
                        </div>
                        <div>
                            <label className="font-medium block mb-1">Gender</label>
                            <Input type="text" name="gender" value={formData.gender} onChange={handleChange} />
                        </div>
                    </div>

                    {/* DOB */}
                    <div>
                        <label className="font-medium block mb-1">Date of Birth</label>
                        <Input type="date" name="dob" value={formData.dob} onChange={handleChange} />
                    </div>

                    {/* Job Title & Start Date */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-medium block mb-1">Job Title</label>
                            <Select value={formData.mainSkillId} onValueChange={(value) => setFormData({ ...formData, mainSkillId: value })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select job title" />
                                </SelectTrigger>
                                <SelectContent>
                                    {skills.map((skill) => (
                                        <SelectItem key={skill.id} value={skill.id.toString()}>
                                            {skill.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <ValidationError field="mainSkillId" />
                        </div>
                        <div>
                            <label className="font-medium block mb-1">Start Date</label>
                            <Input type="date" name="startDate" value={formData.startDate} onChange={handleChange} />
                            <ValidationError field="startDate" />
                        </div>
                    </div>

                    {/* Job Title & Start Date */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-medium block mb-1">Job Title</label>
                            <Select value={formData.mainSkillId} onValueChange={(value) => setFormData({ ...formData, mainSkillId: value })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select job title" />
                                </SelectTrigger>
                                <SelectContent>
                                    {skills.map((skill) => (
                                        <SelectItem key={skill.id} value={skill.id.toString()}>
                                            {skill.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="font-medium block mb-1">Start Date</label>
                            <Input type="date" name="startDate" value={formData.startDate} onChange={handleChange} required />
                        </div>
                    </div>

                    {/* Address */}
                    <div>
                        <label className="font-medium block mb-1">Address</label>
                        <Input type="text" name="addressAttributes.line1" value={formData.addressAttributes.line1} onChange={handleChange} placeholder="Line 1" />
                        <Input type="text" name="addressAttributes.line2" value={formData.addressAttributes.line2} onChange={handleChange} placeholder="Line 2" />
                        <Input type="text" name="addressAttributes.line3" value={formData.addressAttributes.line3} onChange={handleChange} placeholder="Line 3" />
                        <Input type="text" name="addressAttributes.town" value={formData.addressAttributes.town} onChange={handleChange} placeholder="Town" />
                        <Input type="text" name="addressAttributes.county" value={formData.addressAttributes.county} onChange={handleChange} placeholder="County" />
                        <Input type="text" name="addressAttributes.postcode" value={formData.addressAttributes.postcode} onChange={handleChange} placeholder="Postcode" />
                    </div>

                    {/* Payroll Details */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-medium block mb-1">NI Number</label>
                            <Input type="text" name="niNumber" value={formData.niNumber} onChange={handleChange} />
                        </div>
                        <div>
                            <label className="font-medium block mb-1">Payroll Number</label>
                            <Input type="text" name="payrollNumber" value={formData.payrollNumber} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Bank Details */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="font-medium block mb-1">Account Holder Name</label>
                            <Input type="text" name="bankAccountAttributes.accountHolderName" value={formData.bankAccountAttributes.accountHolderName} onChange={handleChange} />
                        </div>
                        <div>
                            <label className="font-medium block mb-1">Sort Code</label>
                            <Input type="text" name="bankAccountAttributes.sortCode" value={formData.bankAccountAttributes.sortCode} onChange={handleChange} />
                        </div>
                        <div className="col-span-2">
                            <label className="font-medium block mb-1">Bank Account Number</label>
                            <Input type="text" name="bankAccountAttributes.bankAccountNumber" value={formData.bankAccountAttributes.bankAccountNumber} onChange={handleChange} />
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex space-x-4">
                        <Button className='bg-brand-500' type="submit" disabled={submitting}>
                            {submitting ? "Creating..." : "Create Candidate"}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => navigate("/")}>
                            Cancel
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
}
